import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, 
  Card, 
  CardHeader, 
  CardContent, 
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField
} from '@mui/material';
import {
  Storage as Server,
  Add as PlusCircle,
  Language as Globe,
  ContentCopy,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useGlobalConfig } from 'context/GlobalContext';
import { bumpAssociatedEnvironmentsRevision, selectPilotList, selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { styled, useTheme } from '@mui/material/styles';
import type { PilotWithEnvironment } from 'apis/pilotAPI';
import { PilotApis } from 'apis/pilotAPI';
import { ProjectEnvironment } from 'types/apis/pilotInterfaces';
import { ProjectAttributesSection } from 'components/project';
import { projectEnvironmentToAppEnv } from 'features/contentManagement/mediaContent/mediaEnvMapping';
import { ExternalLink, FilePenLine } from 'lucide-react';
import { useToast } from 'context/ToastContext';
import { DatabaseZap } from 'lucide-react';
import { LayoutGrid } from 'lucide-react';
import { Workflow } from 'lucide-react';
import { Cog } from 'lucide-react';
import { Info } from 'lucide-react';
import { RefreshCcw } from 'lucide-react';
import { GitCompareArrows } from 'lucide-react';
import { TriangleAlert, CheckCircle2, Loader2 } from 'lucide-react';
import { CirclePlus } from 'lucide-react';
import { EDIT_PROJECT_DATA_KEY } from 'features/utility/UtilityCreationPage';
import { DATA_SOURCE_GROUP_KEY } from 'features/utility/registryUtils';
import { PermissionGate } from 'components/access/PermissionGate';
import { UI_PAGES } from 'framework/access/UiRegistry';

// Helper functions for environment status messages
const getEnvironmentStatusText = (status: string | undefined): string => {
  if (!status) return 'Creation Failed';
  
  switch (status.toLowerCase()) {
    case 'completed':
    case 'active':
      return 'Creation Completed';
    case 'in_progress':
      return 'Creation In Progress';
    case 'pending':
      return 'Creation Pending';
    case 'failed':
      return 'Creation Failed';
    default:
      return 'Creation Failed';
  }
};

const getEnvironmentStatusMessage = (status: string | undefined, stackName?: string): string => {
  if (!status) return `creation has failed.`;
  
  const stackNameText = stackName ? `${stackName} ` : '';
  
  switch (status.toLowerCase()) {
    case 'completed':
    case 'active':
      return `${stackNameText}has been created.`;
    case 'in_progress':
      return `${stackNameText}is still being created.`;
    case 'pending':
      return `${stackNameText}creation is pending.`;
    case 'failed':
      return `${stackNameText}creation has failed.`;
    default:
      return `${stackNameText}creation has failed.`;
  }
};

const getViewButtonText = (status: string | undefined): string => {
  if (!status) return 'Details';
  
  switch (status.toLowerCase()) {
    case 'in_progress':
      return 'Status';
    case 'pending':
      return 'Status';
    case 'failed':
      return 'Error Details';
    default:
      return 'Details';
  }
};


const CompactEnvCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  marginBottom: theme.spacing(2),
  background: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
}));

const BlueBadge = styled(Chip)(({ theme }) => ({
  background: theme.palette.info.main,
  color: theme.palette.info.contrastText,
  fontWeight: 500,
  fontSize: 13,
  height: 24,
  borderRadius: 12,
  '.MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
}));




export const PilotDetailsPage = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const dispatch = useDispatch();
  
  const pilotList = useSelector(selectPilotList);
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const selectedPilot = pilotList.find(pilot => pilot.projectId === selectedPilotId) as PilotWithEnvironment | undefined;
  
  // Check if project type is MIGRATED (case-insensitive)
  const isMigratedProject = selectedPilot?.tag?.toLowerCase().includes('migrated');

  // Project Setup / Project Updates edit live config directly — nonprod/prod can't be written
  // to directly (see DCX staging flows elsewhere), so there's nothing to edit here for them.
  const activeAppEnv = selectedPilot?.environment ? projectEnvironmentToAppEnv(selectedPilot.environment) : null;
  const isStageOnlyEnv = activeAppEnv === 'nonprod' || activeAppEnv === 'prod';

  // Use refs to avoid dependency issues
  const rcRef = useRef(rc);
  const toastRef = useRef(toast);
  
  // Update refs when values change
  useEffect(() => {
    rcRef.current = rc;
    toastRef.current = toast;
  }, [rc, toast]);

  // If we have a selectedPilotId but no matching pilot in the list, 
  // it might be because the pilot list hasn't loaded yet or the pilot was deleted
  const isPilotListLoaded = pilotList.length > 0;
  const isSelectedPilotFound = selectedPilot !== undefined;

  const [associatedEnvironments, setAssociatedEnvironments] = useState<ProjectEnvironment[]>([]);
  const [allEnvironments, setAllEnvironments] = useState<ProjectEnvironment[]>([]);
  const [selectedEnvironmentToAdd, setSelectedEnvironmentToAdd] = useState<ProjectEnvironment | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [environmentToRemove, setEnvironmentToRemove] = useState<ProjectEnvironment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEnvironments, setIsLoadingEnvironments] = useState(false);
  const [isLoadingAssociatedEnvironments, setIsLoadingAssociatedEnvironments] = useState(false);
  const [isAssociatingEnvironment, setIsAssociatingEnvironment] = useState(false);
  const [environmentsError, setEnvironmentsError] = useState<string | null>(null);
  const [associatedEnvironmentsError, setAssociatedEnvironmentsError] = useState<string | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [pushSummary, setPushSummary] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  // Push Updates is handled as a dedicated route (see ROUTE_TREE.PROJECT.PUSH_UPDATES)

  // Build rows for push dialog from global edits slice

  // Memoized function to fetch associated environments
  const fetchAssociatedEnvironments = useCallback(async (projectId: string) => {
    console.log('fetchAssociatedEnvironments called for projectId:', projectId);
    if (!projectId) {
      setAssociatedEnvironments([]);
      return;
    }

    setIsLoadingAssociatedEnvironments(true);
    setAssociatedEnvironmentsError(null);
    
    try {
      const response = await PilotApis.getAssociatedEnvironments(rcRef.current, projectId);
      
      if (response.error) {
        const errorMessage = response.error.message || 'Failed to fetch associated environments';
        setAssociatedEnvironmentsError(errorMessage);
        toastRef.current.error(errorMessage);
      } else if (response.data?.data) {
        setAssociatedEnvironments(response.data?.data);
      } else {
        setAssociatedEnvironments([]);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setAssociatedEnvironmentsError(errorMessage);
      toastRef.current.error(errorMessage);
    } finally {
      setIsLoadingAssociatedEnvironments(false);
    }
  }, []); // Empty dependency array since we're using refs

  // Memoized function to fetch all environments
  const fetchAllEnvironments = useCallback(async () => {
    console.log('fetchAllEnvironments called');
    setIsLoadingEnvironments(true);
    setEnvironmentsError(null);
    
    try {
      const response = await PilotApis.getAllEnvironments(rcRef.current);
      
      if (response.error) {
        const errorMessage = response.error.message || 'Failed to fetch environments';
        setEnvironmentsError(errorMessage);
        toastRef.current.error(errorMessage);
      } else if (response.data?.data) {
        setAllEnvironments(response.data.data);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setEnvironmentsError(errorMessage);
      toastRef.current.error(errorMessage);
    } finally {
      setIsLoadingEnvironments(false);
    }
  }, []); // Empty dependency array since we're using refs

  // Fetch associated environments for the selected pilot
  useEffect(() => {
    if (selectedPilotId) {
      fetchAssociatedEnvironments(selectedPilotId);
    } else {
      setAssociatedEnvironments([]);
    }
  }, [selectedPilotId, fetchAssociatedEnvironments]);

  // Fetch all available environments
  useEffect(() => {
    fetchAllEnvironments();
  }, [fetchAllEnvironments]);

  // Filter out environments that are already associated with the project
  const availableEnvironmentsToAdd = allEnvironments.filter(
    env => !associatedEnvironments.some(assocEnv => assocEnv.id === env.id)
  );

  const handleAddEnvironment = async () => {
    if (!selectedEnvironmentToAdd || !selectedPilotId) {
      return;
    }
    
    setIsAssociatingEnvironment(true);
    
    try {
      const response = await PilotApis.associateEnvironment(rcRef.current, selectedPilotId, selectedEnvironmentToAdd.id);
      
      if (response.error) {
        toastRef.current.error(response.error.message || 'Failed to associate environment');
      } else {
        // Add the environment to the local state
        setAssociatedEnvironments(prev => [...prev, selectedEnvironmentToAdd]);
        setSelectedEnvironmentToAdd(null);
        toastRef.current.success('Environment associated successfully!');
        // Trigger sidebar dropdown refresh (it fetches associated envs independently)
        dispatch(bumpAssociatedEnvironmentsRevision());
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toastRef.current.error(errorMessage);
    } finally {
      setIsAssociatingEnvironment(false);
    }
  };

  const initiateRemoveEnvironment = (envId: string) => {
    const env = associatedEnvironments.find(e => e.id === envId);
    if (env) {
      setEnvironmentToRemove(env);
      setIsConfirmDialogOpen(true);
    }
  };

  const executeRemoveEnvironment = () => {
    if (environmentToRemove) {
      setAssociatedEnvironments(prev => prev.filter(env => env.id !== environmentToRemove.id));
      toastRef.current.success(`"${environmentToRemove.name}" has been disassociated from the pilot.`);
      setEnvironmentToRemove(null);
      setIsConfirmDialogOpen(false);
    }
  };

  // NOTE: Dedicated pages now handle push/review flows; keep this page focused on navigation.
  const navToPushUpdates = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.PUSH_UPDATES);
  };

  const navToUpdateHistory = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.UPDATE_HISTORY);
  };

  const navToIngestionConfigs = () => {
    rc.uiRouter.navigate(ROUTE_TREE.PROJECT.INGESTION_CONFIGS);
  };

  const navToConfigCompare = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.CONFIG_COMPARE);
  };


  if (!isPilotListLoaded) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
          Loading pilots...
        </Typography>
      </Box>
    );
  }

  if (!isSelectedPilotFound) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Selected pilot not found. Please select a pilot from the list.
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => rc.uiRouter.navigate(ROUTE_TREE.PROJECT.LIST)}
          sx={{ mt: 2 }}
        >
          Go to Pilot List
        </Button>
      </Box>
    );
  }


  const handleEditProject = () => {
    if (selectedPilot) {
      setIsLoading(true);
      try {
        // Store the project data for editing
        const editData = {
          ...selectedPilot,
          // Step 1 data from the project
          projectId: selectedPilot.projectId,
          name: selectedPilot.name,
          description: selectedPilot.description,
          status: selectedPilot.status,
          awsRegion: selectedPilot.awsRegion,
          projectImageUrl: selectedPilot.projectImageUrl,
          // Add environment data in array format (for backward compatibility)
          projectEnvironments: [{ id: "", name: selectedPilot.name + " Environment" }],
          // Also include single environment object if available
          environment: selectedPilot.environment,
          projectType: selectedPilot.projectType
        };

        console.log('🔍 Debug - Edit data:', editData);
        
        sessionStorage.setItem(EDIT_PROJECT_DATA_KEY, JSON.stringify(editData));
        
        // Navigate to edit page
        rc.uiRouter.navigate(ROUTE_TREE.PROJECT.EDIT, { projectId: selectedPilot.projectId });
      } finally {
        setIsLoading(false);
      }
    }
  };


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, md: 4 }, bgcolor: 'background.default', minHeight: '100vh', alignItems: 'stretch' }}>
      {/* Page Title */}
      <Typography variant="h5" component="h1" sx={{ mb: 1, textAlign: 'left', fontWeight: 700, color: 'text.primary' }}>
        Project Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left', maxWidth: 900 }}>
        View the detailed attributes and information for the current project.
      </Typography>

      {/* Project Attributes Section */}
      <ProjectAttributesSection
        projectId={selectedPilot.projectId || '—'}
        utilityName={selectedPilot.name || '—'}
        description={selectedPilot.description || '-'}
        ingestionType={
          selectedPilot.unique_groups && selectedPilot.unique_groups.hasOwnProperty(DATA_SOURCE_GROUP_KEY)
            ? (() => {
                const val = selectedPilot.unique_groups[DATA_SOURCE_GROUP_KEY];
                return typeof val === "string" ? val.replace(/_Pilot/g, "") : val;
              })()
            : undefined
        }
      />

      {/* Project Environments Card */}
      <Card sx={{ boxShadow: 1, borderRadius: 2, p: 0, mb: 2, bgcolor: 'background.paper' }}>
        <CardHeader sx={{ pb: 0, pl: 3, pr: 3, pt: 3 }}
          title={<Typography variant="h6" component="h2" sx={{ fontWeight: 600, textAlign: 'left', color: 'text.primary' }}>
            <Server sx={{ mr: 1, color: 'primary.main' }} /> Project Environments
          </Typography>}
          subheader={<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
            Manage environments associated with this project. Currently associated: {associatedEnvironments?.length}.
          </Typography>}
        />
        <CardContent sx={{ pt: 2, pl: 3, pr: 3, pb: 3 }} style={{borderBottom: '1px solid',borderColor: `${theme.palette.divider}`}}>
          {associatedEnvironmentsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {associatedEnvironmentsError}
            </Alert>
          )}
          
          {isLoadingAssociatedEnvironments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading associated environments...
              </Typography>
            </Box>
          ) : associatedEnvironments?.length > 0 ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3,
              width: '100%',
            }}>
              {associatedEnvironments.map(env => (
                <CompactEnvCard key={env.id} sx={{ minWidth: 0, width: '100%' }}>
                  <CardHeader
                    sx={{ pb: 1, pt: 2, pl: 2, pr: 2 }}
                    title={<Typography variant="subtitle1" fontWeight={600} sx={{ textAlign: 'left' }}>{env.environment_name}</Typography>}
                  />
                  <CardContent sx={{ pt: 0, pl: 2, pr: 2, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1,textAlign: 'left' }}>
                      <strong>Type:</strong> {env.type || 'NA'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Globe sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>Region:</strong> {env.awsRegion || 'NA'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'start', mb: 1 }}>
                      <ExternalLink size={14} style={{ marginRight: 8, color: `${theme.palette.primary.main}` }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>URL:</strong> <Link href={env.data_server_url} target="_blank" rel="noopener noreferrer">{env.data_server_url|| "NA"}</Link>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Dashboard:</strong> <Link href={selectedPilot.frontend_url} target="_blank" rel="noopener noreferrer">{selectedPilot.frontend_url || "NA"}</Link>
                      </Typography>
                    </Box>
                    {env.status && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          Status:
                        </Typography>
                        {env?.status?.toLowerCase() === 'in_progress' ? (
                          <BlueBadge
                            onClick={() => {
                              const processId = (env as any).process_id;
                  
                              rc.uiRouter.navigate({
                                path: `${ROUTE_TREE.PROJECT.ENVIRONMENT_CREATION_STATUS.path}?projectId=${selectedPilot.projectId}&processId=${processId}`,
                                name: ROUTE_TREE.PROJECT.ENVIRONMENT_CREATION_STATUS.name
                              });
                            }}
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} sx={{ color: 'white' }} />
                                In Progress
                              </Box>
                            }
                          />
                        ) : (
                          <Chip
                            onClick={() => {
                              const processId = (env as any).process_id;
                              console.log('processId', selectedPilot);
                              rc.uiRouter.navigate({
                                path: `${ROUTE_TREE.PROJECT.ENVIRONMENT_CREATION_STATUS.path}?projectId=${selectedPilot.projectId}&processId=${processId}`,
                                name: ROUTE_TREE.PROJECT.ENVIRONMENT_CREATION_STATUS.name
                              });
                            }}
                            // disabled={env.status?.toLowerCase()!=='failed'}
                            size="small"
                            label={env.status}
                            variant="outlined"
                            color={env.status?.toLowerCase() === 'active'||env.status?.toLowerCase() === 'completed' ? 'success' : 'default'}
                          />
                        )}
                      </Box>
                    )}
                  </CardContent>
                </CompactEnvCard>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 4, border: '2px dashed', borderColor: 'grey.300', borderRadius: 1, textAlign: 'center', width: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No environments currently associated with this project.
              </Typography>
            </Box>
          )}
        </CardContent>
        <CardContent sx={{ p: 3, }}>
          <Typography variant="subtitle1" component="h2" sx={{ mb: 1, color: 'text.primary', textAlign: 'left' }}>
            Add Existing Environment:
          </Typography>
          
          {environmentsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {environmentsError}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Autocomplete
              sx={{ minWidth: 300, flexGrow: 1 }}
              options={availableEnvironmentsToAdd}
              value={selectedEnvironmentToAdd}
              onChange={(event, newValue) => setSelectedEnvironmentToAdd(newValue)}
              getOptionLabel={(option) => {
                const displayName = (option as any).stack_name + " - " + option.environment_name || (option as any).stack_name || `Environment ${option.id}`;
                return displayName;
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isLoadingEnvironments}
              disabled={isLoadingEnvironments}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={isLoadingEnvironments ? 'Loading environments...' : 'Search and select an environment...'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingEnvironments ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const stackName = (option as any).stack_name;
                const envName = option.environment_name;
                const displayName = stackName 
                  ? `${stackName} - ${envName}` 
                  : (envName || `Environment ${option.id}`);
                return (
                  <Box component="li" {...props} key={option.id}>
                    {displayName}
                  </Box>
                );
              }}
              noOptionsText="No environments available"
            />
            <PermissionGate require={UI_PAGES.PROJECT_DETAILS.ctas.add_associated_env.id} mode="disable">
              <Button
                variant="contained"
                startIcon={isAssociatingEnvironment ? <CircularProgress size={16} color="inherit" /> : <CirclePlus color={theme.palette.primary.contrastText} size={16} />}
                onClick={handleAddEnvironment}
                disabled={!selectedEnvironmentToAdd || isLoadingEnvironments || isAssociatingEnvironment}
                sx={{
                  minWidth: 140,
                  py: 1.5,
                  textTransform: 'none',
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              >
                Add to Project
              </Button>
            </PermissionGate>
          </Box>
        </CardContent>
        {/* <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', borderRadius: '0 0 12px 12px', width: '100%' }}>
          <Typography variant="caption" color="text.secondary">
            Changes to associated environments are managed locally and will reflect in the sidebar.
          </Typography>
        </Box> */}
      </Card>

      {/* Project Actions Card - Only show when there's an active or complete environment */}
      {/* {associatedEnvironments.some(env => 
        env.status?.toLowerCase() === 'active' || 
        env.status?.toLowerCase() === 'completed'
      ) && (
        <Card sx={{ boxShadow: 1, borderRadius: 2, p: 0, mb: 2, bgcolor: 'background.paper' }}>
          <CardHeader sx={{ pb: 0, pl: 3, pr: 3, pt: 3 }}
            title={<Typography variant="h6" component="h2" sx={{ fontWeight: 600, textAlign: 'left', color: 'text.primary' }}>
              Project Actions
            </Typography>}
            subheader={<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
              Save or submit the current project configuration.
            </Typography>}
          />
          <CardContent sx={{ pt: 2, pl: 3, pr: 3, pb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <BglPrimaryButton
                onClick={handleSubmitPilot}
                startIcon={isLoading ? <CircularProgress size={16} /> : <Send />}
                disabled={isLoading}
              >
                Push Configs to UAT
              </BglPrimaryButton>
            </Box>
          </CardContent>
        </Card>
      )} */}
      {/* Project Setup Section — hidden for nonprod/prod, which can't be edited directly */}
      {!isStageOnlyEnv && (
        <Card sx={{ boxShadow: 1, borderRadius: 2, p: 0, mb: 3, bgcolor: 'background.paper' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Cog style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={24} />
              <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Project Setup
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3,textAlign: 'left' }}>
              Edit different parts of the project's configuration.
            </Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 3,
            }}>
              {/* Core Attributes, Ingestion Configurations, Pipeline Components - Hide for migrated projects */}
              {!isMigratedProject && (
              <>
              {/* Core Attributes */}
              <Card 
                onClick={handleEditProject}
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '.5rem',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2,'&:last-child': {
                    paddingBottom: 2,
                  } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FilePenLine style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={24} />
                    
                    <Typography variant="h6">
                      Core Attributes
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="text.secondary">›</Typography>
                </CardContent>
              </Card>

              {/* Ingestion Configurations */}
              <Card 
                onClick={navToIngestionConfigs}
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '.5rem',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2,'&:last-child': {
                    paddingBottom: 2,
                  } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DatabaseZap style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={24} />
                    <Typography variant="h6">
                      Ingestion Configurations
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="text.secondary">›</Typography>
                </CardContent>
              </Card>

              {/* Pipeline Components */}
              <Card 
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.PROJECT.PIPELINE_COMPONENTS)}
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '.5rem',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  p: 2,
                  '&:last-child': {
                    paddingBottom: 2,
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Workflow style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={24} />
                    <Typography variant="h6">
                      Pipeline Components
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="text.secondary">›</Typography>
                </CardContent>
              </Card>
              </>
              )}

              {/* Application Scope */}
              <Card 
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  borderRadius: '.5rem',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => rc.uiRouter.navigate(ROUTE_TREE.PROJECT.APPLICATION_SCOPE)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2,'&:last-child': {
                    paddingBottom: 2,
                  } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LayoutGrid style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={24} />
                    <Typography variant="h6">
                      Application Scope
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="text.secondary">›</Typography>
                </CardContent>
              </Card>

            </Box>
          </CardContent>
        </Card>
      )}

      {/* Project Updates Section — hidden for nonprod/prod, same reason as Project Setup above.
          environment-specific cards also hidden for migrated projects; Compare & Sync stays available */}
      {!isStageOnlyEnv && (
        <Card sx={{ boxShadow: 1, borderRadius: 2, p: 0, mb: 3, bgcolor: 'background.paper' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Info style={{ marginRight: 16, color: `${theme.palette.primary.main}` }} size={24} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Project Updates
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3,textAlign: 'left' }}>
              {isMigratedProject
                ? 'Actionable updates for this project.'
                : `Actionable updates for the selected '${selectedPilot?.environment?.stack_name}' environment.`}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
              {!isMigratedProject && (
              <>
              {/* Pending Configuration Push */}
              <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2,'&:last-child': {
                    paddingBottom: 2,
                  } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500,textAlign: 'left',display: 'flex', alignItems: 'center' }}>
                      <RefreshCcw style={{ marginRight: 8, color: `${theme.palette.primary.main}` }} size={16} />
                        Pending Configuration Push
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left' }}>
                        You have updated configs that need to be pushed to <strong>{selectedPilot?.environment?.stack_name}</strong>.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    onClick={navToPushUpdates}
                    variant="contained"
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    Push Updates
                  </Button>
                </CardContent>
              </Card>

              {/* Update History */}
              <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, '&:last-child': { paddingBottom: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                        <Info style={{ marginRight: 8, color: `${theme.palette.primary.main}` }} size={16} />
                        Update History
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        View the complete history of configuration updates for this project.
                      </Typography>
                    </Box>
                  </Box>
                  <Button onClick={navToUpdateHistory} variant="outlined" size="small" sx={{ minWidth: 140 }}>
                    View
                  </Button>
                </CardContent>
              </Card>
              </>
              )}

              {/* Compare & Sync Environments - shown for migrated projects too */}
              <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, '&:last-child': { paddingBottom: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                        <GitCompareArrows style={{ marginRight: 8, color: `${theme.palette.primary.main}` }} size={16} />
                        Compare &amp; Sync Environments
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                        Compare configurations between two environments and synchronize them.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    onClick={navToConfigCompare}
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 140 }}
                  >
                    Compare
                  </Button>
                </CardContent>
              </Card>

              {!isMigratedProject && (
              /* Environment Creation In Progress */
              <Card variant="outlined" sx={{ backgroundColor: 'background.default' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2,'&:last-child': {
                    paddingBottom: 2,
                  } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500,textAlign: 'left',display: 'flex', alignItems: 'center' }}>
                        {(() => {
                          const status = selectedPilot?.environment?.status?.toLowerCase();
                          if (status === "completed" || status === "active") {
                            return <CheckCircle2 style={{ marginRight: 8, color: theme.palette.success.main }} size={16} />;
                          } else if (status === "in_progress") {
                            return (<Box component="span" sx={{ display: 'inline-flex', marginRight: 1, animation: 'spin 2s linear infinite' }}>
                              <Loader2 style={{ color: theme.palette.warning.main }} size={16} />
                            </Box>)
                          } else if (status === "pending") {
                            return (<Box component="span" sx={{ display: 'inline-flex', marginRight: 1 }}>
                              <Loader2 style={{ color: theme.palette.info.main }} size={16} />
                            </Box>)
                          } else {
                            return (
                              <TriangleAlert style={{ marginRight: 8, color: theme.palette.error.main }} size={16} />
                            )
                          }
                        })()}
                        Environment {getEnvironmentStatusText(selectedPilot?.environment?.status)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left' }}>
                        The new environment {getEnvironmentStatusMessage(selectedPilot?.environment?.status, selectedPilot?.environment?.stack_name)}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 120 }}
                    onClick={() => {
                      rc.uiRouter.navigate({
                        path: `${ROUTE_TREE.PROJECT.ENVIRONMENT_CREATION_STATUS.path}?projectId=${selectedPilot.projectId}&processId=${selectedPilot?.environment?.process_id}`,
                        name: ROUTE_TREE.PROJECT.ENVIRONMENT_CREATION_STATUS.name
                      });
                    }}
                  >
                    View {getViewButtonText(selectedPilot?.environment?.status)}
                  </Button>
                </CardContent>
              </Card>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to remove the environment "{environmentToRemove?.name}" from this project?
            Removing it might change the selected environment in the sidebar if this one was active.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsConfirmDialogOpen(false);
            setEnvironmentToRemove(null);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={executeRemoveEnvironment} 
            color="error" 
            variant="contained"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Push Summary Modal */}
      <Dialog open={summaryOpen} onClose={() => setSummaryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configs Push Summary</DialogTitle>
        <DialogContent dividers>
          {pushSummary ? (
            <Box sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {pushSummary.message || 'Summary'}
              </Typography>
              <Box sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                maxHeight: 400,
                overflow: 'auto'
              }}>
                <pre style={{ margin: 0 }}>
{JSON.stringify(pushSummary, null, 2)}
                </pre>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2">No summary available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            startIcon={<ContentCopy />}
            onClick={async () => {
              try {
                const text = JSON.stringify(pushSummary ?? {}, null, 2);
                if (navigator.clipboard && window.isSecureContext) {
                  await navigator.clipboard.writeText(text);
                } else {
                  const ta = document.createElement('textarea');
                  ta.value = text;
                  ta.style.position = 'fixed';
                  ta.style.left = '-9999px';
                  document.body.appendChild(ta);
                  ta.focus();
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                }
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              } catch (_) {
                // ignore
              }
            }}
          >
            {copied ? 'Copied!' : 'Copy JSON'}
          </Button>
          <Button onClick={() => setSummaryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 